import { useNavigate } from "react-router-dom";
import { FileText, Loader2, AlertTriangle } from "lucide-react";
import { RiskBadge, DocTypeBadge, ReviewBadge } from "./RiskBadge";

export const DocumentList = ({ documents, showUploader = false, total = 0, onLoadMore }) => {
  const navigate = useNavigate();

  if (documents.length === 0)
    return (
      <div data-testid="empty-documents" className="bg-white border border-[#E8E6E1] rounded-lg p-12 text-center">
        <FileText className="w-8 h-8 text-[#C9CFC9] mx-auto" strokeWidth={1.5} />
        <p className="mt-3 text-[#57605A]">No documents yet. Upload one or load demo data to see Nexari in action.</p>
      </div>
    );

  return (
    <div className="space-y-3">
      {documents.map((doc, i) => (
        <div
          key={doc.id}
          data-testid={`document-row-${doc.id}`}
          onClick={() => navigate(`/documents/${doc.id}`)}
          className="fade-in-up cursor-pointer bg-white border border-[#E8E6E1] rounded-lg p-5 flex flex-wrap items-center gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <div className="w-10 h-10 bg-[#D6E0D9]/40 rounded-md flex items-center justify-center shrink-0">
            {doc.status === "processing" ? (
              <Loader2 className="w-5 h-5 text-[#C48231] animate-spin" strokeWidth={1.5} />
            ) : doc.status === "failed" ? (
              <AlertTriangle className="w-5 h-5 text-[#B54D40]" strokeWidth={1.5} />
            ) : (
              <FileText className="w-5 h-5 text-[#2E5C50]" strokeWidth={1.5} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-[#1C211F] truncate">{doc.file_name}</p>
            <p className="text-sm text-[#57605A]">
              {doc.client_name && <span>{doc.client_name} · </span>}
              {showUploader && <span>by {doc.uploader_name} · </span>}
              {new Date(doc.created_at).toLocaleDateString()}
              {doc.is_demo && <span className="ml-2 text-[#C07A60] font-medium">DEMO</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {doc.status === "processing" && (
              <span className="text-xs font-semibold uppercase tracking-wider text-[#C48231]">Analyzing…</span>
            )}
            {doc.status === "failed" && (
              <span className="text-xs font-semibold uppercase tracking-wider text-[#B54D40]">Analysis failed</span>
            )}
            <DocTypeBadge type={doc.document_type} />
            <RiskBadge level={doc.risk_level} />
            {doc.status === "analyzed" && <ReviewBadge status={doc.review_status} />}
          </div>
        </div>
      ))}
      {onLoadMore && total > documents.length && (
        <button
          data-testid="load-more-documents"
          onClick={onLoadMore}
          className="w-full bg-white border border-[#E8E6E1] hover:border-[#2E5C50] text-[#2E5C50] transition-colors duration-200 rounded-lg py-2.5 text-sm font-medium"
        >
          Load more ({documents.length} of {total})
        </button>
      )}
    </div>
  );
};
